/**
 * EditablePage.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import { getIcon } from '../utils/icons';
import ContactFormModal from './ContactFormModal';
import LoadingSpinner from './LoadingSpinner';
import { SITE_TAGLINE } from '../constants/siteTagline';
import { useLanguage } from '../context/LanguageContext';
import { getTranslatedPage } from '../i18n/pages';
import ShareFloatingButton from './ShareFloatingButton';
import ShareModal from './ShareModal';
import './EditablePage.css';
import './PageViewer.css';
import './Contact.css';
import './Home.css';

/**
 * @param {string} pageId — CMS page id (e.g. home, about)
 * @param {string} pageTitle — fallback title label
 * @param {string} [floatingSharePath] — public path for shared link (e.g. / or /about); when set, shows floating share
 */
const COMPACT_PAGE_IDS = [
  'home',
  'about',
  'contact',
  'faq',
  'return-policy',
  'shipping-policy',
  'privacy-policy',
  'terms-of-use',
];

const EditablePage = ({ pageId, pageTitle, floatingSharePath }) => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { showError, showSuccess } = useToast();
  const { currentUser, hasAnyRole } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const contactButtonRef = React.useRef(null);

  // Check if user can edit pages
  const canEdit = currentUser && currentUser.roles && (
    // Check if roles is array of strings
    (Array.isArray(currentUser.roles) && typeof currentUser.roles[0] === 'string' &&
      ['admin', 'super_admin', 'content_creator'].some(role => currentUser.roles.includes(role))) ||
    // Check if roles is array of objects with name property
    (hasAnyRole && hasAnyRole(['admin', 'super_admin', 'content_creator']))
  );

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  // Move contact button into placeholder for contact page
  useEffect(() => {
    if (pageId === 'contact' && contactButtonRef.current) {
      const placeholder = document.getElementById('contact-button-placeholder');
      if (placeholder && contactButtonRef.current) {
        placeholder.appendChild(contactButtonRef.current);
      }
    }
  }, [pageId, pageData]);

  const loadPage = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/content/pages/${pageId}`);
      
      if (!response.ok) {
        throw new Error('Failed to load page');
      }

      const data = await response.json();
      setPageData(data);
    } catch (error) {
      console.error('Error loading page:', error);
      showError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const containerClass = `page-viewer-container container page-${pageId}${COMPACT_PAGE_IDS.includes(pageId) ? ' page-compact' : ''}`;

  if (loading) {
    return (
      <div className={containerClass}>
        <div className="page-viewer-content content-card">
          <LoadingSpinner
            message={t.ui.page.loading}
            subtitle={SITE_TAGLINE}
          />
        </div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className={containerClass}>
        <div className="page-viewer-content content-card">
          <div className="error">
            <h2>{t.ui.page.notFoundTitle}</h2>
            <p>{t.ui.page.notFoundText}</p>
          </div>
        </div>
      </div>
    );
  }

  // Non-English locales render a translated body in place of the English CMS HTML
  const translatedPage = getTranslatedPage(pageId, language);
  const bodyHtml = translatedPage ? translatedPage.html : pageData.html;

  // Check if this is a footer/legal page that should show the contact section
  const isLegalPage = ['privacy-policy', 'terms-of-use', 'return-policy', 'shipping-policy', 'faq'].includes(pageId);
  const isContactPage = pageId === 'contact';

  return (
    <>
      <div className={containerClass}>
        <div className="page-viewer-content content-card">
          {/* Edit button for admin/content creators */}
          {canEdit && (
            <button 
              className="edit-page-btn"
              onClick={() => navigate(`/admin/pages/edit?pageId=${pageId}`)}
              title={t.ui.page.editTitle}
            >
              {getIcon('edit')} {t.ui.page.edit}
            </button>
          )}

          {floatingSharePath && (
            <ShareFloatingButton
              title={pageData.title || pageTitle}
              url={`${window.location.origin}${floatingSharePath.startsWith('/') ? floatingSharePath : `/${floatingSharePath}`}`}
              text={pageData.title || pageTitle}
              openEmailModal={() => setShowShareModal(true)}
              showSuccess={showSuccess}
            />
          )}

          
          {/* Page content */}
          <div 
            className="page-content"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />

          {/* Contact section for legal/footer pages */}
          {isLegalPage && (
            <div className="page-contact-section">
              <hr className="page-divider" />
              <div className="contact-cta">
                <h3>{t.ui.page.haveQuestions}</h3>
                <p>{t.ui.page.legalQuestions}</p>
                <Link to="/contact" className="btn-contact-link">
                  📧 {t.ui.page.contactUs}
                </Link>
                <div className="contact-quick-info">
                  <p><strong>{t.ui.page.emailLabel}</strong> <a href="mailto:cranfordpub@gmail.com">cranfordpub@gmail.com</a></p>
                  <p><strong>{t.ui.page.phoneLabel}</strong> <a href="tel:19029292391">1-902-929-2391</a> </p>
                </div>
              </div>
            </div>
          )}

          {/* Contact form button for contact page */}
          {isContactPage && (
            <div className="contact-form-cta" ref={contactButtonRef}>
              <button 
                className="btn-open-contact-form"
                onClick={() => setIsContactModalOpen(true)}
                type="button"
              >
                {t.ui.contact.openForm}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contact Form Modal */}
      {isContactPage && (
        <ContactFormModal 
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
        />
      )}

      {floatingSharePath && pageData && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          contentTitle={pageData.title || pageTitle}
          contentUrl={`${window.location.origin}${floatingSharePath.startsWith('/') ? floatingSharePath : `/${floatingSharePath}`}`}
          contentId={pageId}
          contentType="article"
          contentDescription={pageData.description || ''}
        />
      )}
    </>
  );
};

export default EditablePage;
